// Variable editor — slide-out drawer for tuning model inputs.
// First pass covers top-level scalars + per-tier perks + per-scenario behavior.
// Regions, channels, Pulsar Points, agentic, agenticInfra, and rail assumptions
// land in pass 2.
//
// State lives in App.jsx (`inputs` + `setInputs`). This component is purely a
// view + edit surface — no localStorage logic, no draft staging. Edits
// propagate live to the parent on every keystroke; charts re-render
// immediately. Validation runs live and surfaces errors in a banner without
// blocking edits.

import { useEffect, useState } from 'react';
import { DEFAULTS, validateInputs } from '../../engine/index.js';
import Field from './Field.jsx';
import FieldGroup from './FieldGroup.jsx';
import WirexSection from './editor/WirexSection.jsx';
import PointsSection from './editor/PointsSection.jsx';
import ReferralsSection from './editor/ReferralsSection.jsx';
import RegionsSection from './editor/RegionsSection.jsx';
import AgenticSection from './editor/AgenticSection.jsx';
import ChannelsSection from './editor/ChannelsSection.jsx';
import CardProductionSection from './editor/CardProductionSection.jsx';
import DropsSection from './editor/DropsSection.jsx';
import { NumInput } from './editor/primitives.jsx';

const TIER_KEYS = ['free', 'pro'];
const SCENARIO_KEYS = ['conservative', 'base', 'bull'];

function deepClone(o) {
  return JSON.parse(JSON.stringify(o));
}

function setIn(obj, dotPath, value) {
  const parts = dotPath.split('.');
  const next = deepClone(obj);
  let cur = next;
  for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
  cur[parts[parts.length - 1]] = value;
  return next;
}

export default function VariableEditor({ open, onClose, inputs, onChange, onResetAll }) {
  const [errors, setErrors] = useState([]);

  // Debounce live validation — keystroke parsers (e.g., "0.0" mid-typing "0.06")
  // can transiently break sum-to-1 invariants. 200ms swallows those blips
  // without making the banner feel laggy.
  useEffect(() => {
    const id = setTimeout(() => {
      setErrors(validateInputs(inputs));
    }, 200);
    return () => clearTimeout(id);
  }, [inputs]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const set = (path, value) => {
    onChange(setIn(inputs, path, value));
  };

  const resetSection = (section) => {
    onChange({ ...inputs, [section]: deepClone(DEFAULTS[section]) });
  };

  const handleResetAll = () => {
    if (!window.confirm('Reset every assumption to defaults? Your tweaks will be lost.')) return;
    onResetAll?.();
  };

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden={!open}
        className={`fixed inset-0 z-40 bg-carbon-900/40 backdrop-blur-sm transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Edit model assumptions"
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[860px] xl:w-[1040px] bg-paper shadow-2xl border-l border-carbon-100 flex flex-col transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <DrawerHeader onClose={onClose} onResetAll={handleResetAll} />

        <div className="flex-1 overflow-y-auto nice-scroll px-6 py-6 space-y-9">
          {errors.length > 0 && <ErrorBanner errors={errors} />}

          <SectionShell
            title="General"
            caption="Top-level scalars that don't fit a single tier or scenario"
          >
            <FieldGroup title="Behavior + bootstrap">
              <Field
                label="Fixed operating cost ($/mo)"
                value={inputs.fixedOpexMonthly ?? 0}
                step={5000}
                onChange={(v) => set('fixedOpexMonthly', v)}
              />
              <Field
                label="Card-funded top-up share"
                value={inputs.cardFundedTopupShare ?? 0.1}
                pct
                step={0.01}
                onChange={(v) => set('cardFundedTopupShare', v)}
              />
              <Field
                label="USD display anchor"
                value={inputs.fx.eurToUsd}
                step={0.01}
                decimals={3}
                onChange={(v) => set('fx.eurToUsd', v)}
              />
              <Field
                label="GBP display rate"
                value={inputs.fx.eurToGbp}
                step={0.01}
                decimals={3}
                onChange={(v) => set('fx.eurToGbp', v)}
              />
              <Field
                label="Starting users"
                value={inputs.startingUsers}
                step={50}
                onChange={(v) => set('startingUsers', v)}
              />
              <Field
                label="Starting Pro mix"
                value={inputs.startingProMix}
                pct
                step={0.005}
                onChange={(v) => set('startingProMix', v)}
              />
              <Field
                label="Starting cum. card volume ($)"
                value={inputs.startingCumGmv}
                step={10000}
                onChange={(v) => set('startingCumGmv', v)}
              />
            </FieldGroup>
            <FieldGroup title="Stablecoin economics">
              <Field
                label="Smart contract yield APY"
                value={inputs.stablecoin?.smartContractYieldApy ?? 0}
                pct
                step={0.001}
                onChange={(v) => set('stablecoin.smartContractYieldApy', v)}
              />
              <Field
                label="Avg network cost / wallet operation"
                value={inputs.stablecoin?.avgNetworkCostPerWalletOperation ?? 0}
                step={0.05}
                decimals={2}
                onChange={(v) => set('stablecoin.avgNetworkCostPerWalletOperation', v)}
              />
            </FieldGroup>
            <FieldGroup title="Acquisition">
              <Field
                label="Free card activation cost"
                value={inputs.acquisition?.cardActivationCost?.free ?? 0}
                step={1}
                onChange={(v) => set('acquisition.cardActivationCost.free', v)}
              />
              <Field
                label="Pro card activation cost"
                value={inputs.acquisition?.cardActivationCost?.pro ?? 0}
                step={1}
                onChange={(v) => set('acquisition.cardActivationCost.pro', v)}
              />
            </FieldGroup>
          </SectionShell>

          <SectionShell
            title="Tiers"
            caption="Free / Pro perks. All tier economics are editable."
            onReset={() => resetSection('tiers')}
          >
            <div className="grid gap-5 lg:grid-cols-2">
              {TIER_KEYS.map((tk) => (
                <TierPanel key={tk} tierKey={tk} tier={inputs.tiers[tk]} set={set} />
              ))}
            </div>
          </SectionShell>

          <SectionShell
            title="Card production"
            caption="Pulsar's per-card production + shipping cost and tier adoption %"
            onReset={() => resetSection('cardProduction')}
          >
            <CardProductionSection cardProduction={inputs.cardProduction} set={set} />
          </SectionShell>

          <SectionShell
            title="Wirex model assumptions"
            caption="Tier ladder, FX passthrough, monthly floor, Earn / Exchange terms"
            onReset={() => resetSection('wirex')}
          >
            <WirexSection wirex={inputs.wirex} set={set} />
          </SectionShell>

          <SectionShell
            title="Pulsar Points"
            caption="Earn rates, redemption mix, real cost per redemption, breakage"
            onReset={() => resetSection('points')}
          >
            <PointsSection points={inputs.points} set={set} />
          </SectionShell>

          <SectionShell
            title="Referrals"
            caption="Hybrid program — activation bonus + 12-month ongoing share, tier-multiplied"
            onReset={() => resetSection('referral')}
          >
            <ReferralsSection referral={inputs.referral} set={set} />
          </SectionShell>

          <SectionShell
            title="Money Back"
            caption="One economic program for every money-back campaign"
            onReset={() => resetSection('drops')}
          >
            <DropsSection drops={inputs.drops} set={set} />
          </SectionShell>

          <SectionShell
            title="Regions"
            caption="Per-region behavior + 3-year regional mix of net-new users"
            onReset={() => {
              resetSection('regions');
              resetSection('regionalMix');
            }}
          >
            <RegionsSection regions={inputs.regions} regionalMix={inputs.regionalMix} set={set} />
          </SectionShell>

          <SectionShell
            title="AI agentic"
            caption="Per-tier runs / overage, outcome-based savings, merchant referral, B2B Agentic Infra toggle"
            onReset={() => {
              resetSection('aiAgents');
              resetSection('agenticInfra');
            }}
          >
            <AgenticSection
              aiAgents={inputs.aiAgents}
              agenticInfra={inputs.agenticInfra}
              set={set}
            />
          </SectionShell>

          <SectionShell
            title="Channels"
            caption="Net-new acquisition mix and per-channel CPA"
            onReset={() => resetSection('channelMix')}
          >
            <ChannelsSection channelMix={inputs.channelMix} set={set} />
          </SectionShell>

          <SectionShell
            title="Scenarios"
            caption="Pessimistic / Mean / Optimistic behavioral assumptions + adoption distributions"
            onReset={() => resetSection('scenarios')}
          >
            <div className="grid gap-5 lg:grid-cols-3">
              {SCENARIO_KEYS.map((sk) => (
                <ScenarioPanel
                  key={sk}
                  scenarioKey={sk}
                  scenario={inputs.scenarios[sk]}
                  set={set}
                />
              ))}
            </div>
          </SectionShell>
        </div>

        <DrawerFooter onClose={onClose} hasErrors={errors.length > 0} />
      </aside>
    </>
  );
}

function DrawerHeader({ onClose, onResetAll }) {
  return (
    <header className="px-6 py-5 border-b border-carbon-100 flex items-center justify-between bg-white">
      <div>
        <p className="caption text-carbon-400">Edit assumptions</p>
        <h2 className="h3 text-carbon-800 mt-1">Tune the model</h2>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onResetAll}
          className="h-9 px-3.5 rounded-full label text-carbon-700 border border-carbon-200 hover:bg-carbon-50 transition-colors"
        >
          Reset all
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close editor"
          className="h-9 w-9 rounded-full grid place-items-center text-carbon-700 hover:bg-carbon-50 transition-colors"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </header>
  );
}

function DrawerFooter({ onClose, hasErrors }) {
  return (
    <footer className="px-6 py-4 border-t border-carbon-100 bg-white flex items-center justify-between gap-3">
      <p className="body text-carbon-500">
        {hasErrors
          ? 'Errors above are showing what the validator caught. Edits still propagate live; fix to clear.'
          : 'Edits propagate to charts live. Saved automatically.'}
      </p>
      <button
        type="button"
        onClick={onClose}
        className="h-9 px-4 rounded-full label bg-carbon-800 text-white hover:bg-carbon-900 transition-colors"
      >
        Done
      </button>
    </footer>
  );
}

function ErrorBanner({ errors }) {
  return (
    <section className="rounded-box border border-rose-200 bg-rose-50 p-4">
      <p className="label text-rose-800 mb-2">
        {errors.length} validation {errors.length === 1 ? 'error' : 'errors'}
      </p>
      <ul className="space-y-1">
        {errors.slice(0, 8).map((e) => (
          <li key={e.path + e.message} className="body text-rose-900 num-tabular">
            <span className="label">{e.path || '(root)'}</span> · {e.message}
          </li>
        ))}
        {errors.length > 8 && (
          <li className="body text-rose-700">…and {errors.length - 8} more.</li>
        )}
      </ul>
    </section>
  );
}

function SectionShell({ title, caption, onReset, children }) {
  return (
    <section>
      <div className="flex items-end justify-between mb-3">
        <div>
          <p className="label text-carbon-800">{title}</p>
          {caption && <p className="caption text-carbon-400 mt-1">{caption}</p>}
        </div>
        {onReset && (
          <button
            type="button"
            onClick={onReset}
            className="label text-carbon-500 hover:text-carbon-800 transition-colors"
          >
            Reset section
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function TierPanel({ tierKey, tier, set }) {
  const setT = (field, value) => set(`tiers.${tierKey}.${field}`, value);
  return (
    <FieldGroup title={tier.displayName}>
      <Field
        label="Price ($/mo)"
        value={tier.price}
        step={0.5}
        onChange={(v) => setT('price', v)}
      />
      <Field
        label="Price ($/yr)"
        value={tier.priceAnnual}
        step={5}
        onChange={(v) => setT('priceAnnual', v)}
      />
      <Field
        label="FX cap (USD/mo)"
        value={tier.fxCap}
        step={50}
        onChange={(v) => setT('fxCap', v)}
      />
      <Field
        label="FX above cap"
        value={tier.fxAboveRate}
        pct
        step={0.001}
        onChange={(v) => setT('fxAboveRate', v)}
      />
      <Field
        label="Top-up cap ($/mo)"
        value={tier.topupCap}
        step={100}
        onChange={(v) => setT('topupCap', v)}
      />
      <Field
        label="Top-up above cap"
        value={tier.topupAboveRate}
        pct
        step={0.001}
        onChange={(v) => setT('topupAboveRate', v)}
      />
      <Field
        label="Conversion cap ($/mo)"
        value={tier.swapCap}
        step={100}
        onChange={(v) => setT('swapCap', v)}
      />
      <Field
        label="Conversion above cap"
        value={tier.swapAboveRate}
        pct
        step={0.001}
        onChange={(v) => setT('swapAboveRate', v)}
      />
      <Field
        label="AI runs included"
        value={tier.aiAgentRunsIncluded}
        step={5}
        onChange={(v) => setT('aiAgentRunsIncluded', v)}
      />
      <Field
        label="AI overage ($/run)"
        value={tier.aiAgentOverageRate}
        step={0.01}
        decimals={3}
        onChange={(v) => setT('aiAgentOverageRate', v)}
      />
      <Field
        label="Earn APY"
        value={tier.earnApy}
        pct
        step={0.005}
        onChange={(v) => setT('earnApy', v)}
      />
      <Field
        label="Earn APY · Points"
        value={tier.earnApyPointsBonus}
        pct
        step={0.001}
        onChange={(v) => setT('earnApyPointsBonus', v)}
      />
      <Field
        label="Earn AUM / user ($)"
        value={tier.earnAumPerUser}
        step={50}
        onChange={(v) => setT('earnAumPerUser', v)}
      />
      <Field
        label="Pulsar yield spread"
        value={tier.pulsarYieldSpread ?? 0}
        pct
        step={0.0005}
        onChange={(v) => setT('pulsarYieldSpread', v)}
      />
      <Field
        label="Wallet operation cap ($/mo)"
        value={tier.walletOperationCostCap ?? 0}
        step={1}
        onChange={(v) => setT('walletOperationCostCap', v)}
      />
      <Field
        label="Free ATM cap ($/mo)"
        value={tier.freeAtmCap}
        step={50}
        onChange={(v) => setT('freeAtmCap', v)}
      />
      <Field
        label="Virtual cards"
        value={tier.virtualCardsCount}
        step={1}
        onChange={(v) => setT('virtualCardsCount', v)}
      />
      <Field
        label="Physical card cost"
        value={tier.physicalCardCost}
        step={1}
        onChange={(v) => setT('physicalCardCost', v)}
      />
    </FieldGroup>
  );
}

function ScenarioPanel({ scenarioKey, scenario, set }) {
  const setS = (field, value) => set(`scenarios.${scenarioKey}.${field}`, value);
  const label =
    scenarioKey === 'conservative' ? 'Pessimistic' : scenarioKey === 'bull' ? 'Optimistic' : 'Mean';
  return (
    <FieldGroup title={label}>
      <Field
        label="Net new / mo (M1)"
        value={scenario.netNewM1}
        step={50}
        onChange={(v) => setS('netNewM1', v)}
      />
      <Field
        label="Net new / mo (M36)"
        value={scenario.netNewM36}
        step={100}
        onChange={(v) => setS('netNewM36', v)}
      />
      <Field
        label="Pro conversion"
        value={scenario.proConv}
        pct
        step={0.005}
        onChange={(v) => setS('proConv', v)}
      />
      <Field
        label="Churn — Free"
        value={scenario.churnFree}
        pct
        step={0.005}
        onChange={(v) => setS('churnFree', v)}
      />
      <Field
        label="Churn — Pro"
        value={scenario.churnPro}
        pct
        step={0.005}
        onChange={(v) => setS('churnPro', v)}
      />
      <Field
        label="Active rate"
        value={scenario.activeRate}
        pct
        step={0.01}
        onChange={(v) => setS('activeRate', v)}
      />
      <Field
        label="Card activation rate"
        value={scenario.cardActivationRate ?? 0}
        pct
        step={0.01}
        onChange={(v) => setS('cardActivationRate', v)}
      />
      <Field
        label="Free spend ($/mo)"
        value={scenario.freeSpend}
        step={50}
        onChange={(v) => setS('freeSpend', v)}
      />
      <Field
        label="Pro spend ($/mo)"
        value={scenario.proSpend}
        step={100}
        onChange={(v) => setS('proSpend', v)}
      />
      <Field
        label="Cross-border share"
        value={scenario.xborderPct}
        pct
        step={0.01}
        onChange={(v) => setS('xborderPct', v)}
      />
      <Field
        label="Free trading vol"
        value={scenario.freeTrading}
        step={25}
        onChange={(v) => setS('freeTrading', v)}
      />
      <Field
        label="Pro trading vol"
        value={scenario.proTrading}
        step={100}
        onChange={(v) => setS('proTrading', v)}
      />
      <Field
        label="Free top-up vol"
        value={scenario.freeTopup}
        step={50}
        onChange={(v) => setS('freeTopup', v)}
      />
      <Field
        label="Pro top-up vol"
        value={scenario.proTopup}
        step={100}
        onChange={(v) => setS('proTopup', v)}
      />
      <Field
        label="Free AI msgs / day"
        value={scenario.freeAiActual}
        step={0.5}
        decimals={1}
        onChange={(v) => setS('freeAiActual', v)}
      />
      <Field
        label="Pro AI msgs / day"
        value={scenario.proAiActual}
        step={1}
        onChange={(v) => setS('proAiActual', v)}
      />
      <Field
        label="Earn adoption rate"
        value={scenario.earnAdoptionRate}
        pct
        step={0.01}
        onChange={(v) => setS('earnAdoptionRate', v)}
      />
      <Field
        label="Free wallet ops / mo"
        value={scenario.freeWalletOperations ?? 0}
        step={0.5}
        decimals={1}
        onChange={(v) => setS('freeWalletOperations', v)}
      />
      <Field
        label="Pro wallet ops / mo"
        value={scenario.proWalletOperations ?? 0}
        step={0.5}
        decimals={1}
        onChange={(v) => setS('proWalletOperations', v)}
      />
      <SubsidiesDetails subsidies={scenario.subsidies} setS={setS} />
    </FieldGroup>
  );
}

function SubsidiesDetails({ subsidies, setS }) {
  return (
    <details className="border-t border-carbon-100">
      <summary className="px-4 py-3 label text-carbon-700 cursor-pointer hover:bg-carbon-50 transition-colors">
        Adoption distribution · subsidies
      </summary>
      <div className="px-4 pb-4 pt-2 space-y-3">
        <SubsidyTriplet
          label="FX cap hit rate"
          subsidy={subsidies.fxCapHitRate}
          field="fxCapHitRate"
          setS={setS}
        />
        <SubsidyTriplet
          label="Top-up heavy users"
          subsidy={subsidies.topupHeavyUserRate}
          field="topupHeavyUserRate"
          setS={setS}
        />
        <SubsidyTriplet
          label="Conversion heavy users"
          subsidy={subsidies.swapHeavyUserRate}
          field="swapHeavyUserRate"
          setS={setS}
        />
        <SubsidyTriplet
          label="AI agent heavy users"
          subsidy={subsidies.aiAgentHeavyUserRate}
          field="aiAgentHeavyUserRate"
          setS={setS}
        />
      </div>
    </details>
  );
}

function SubsidyTriplet({ label, subsidy, field, setS }) {
  return (
    <div>
      <p className="label text-carbon-700 mb-1.5">{label}</p>
      <div className="grid grid-cols-3 gap-2">
        {['p25', 'p50', 'p75'].map((p) => (
          <div key={p}>
            <p className="caption text-carbon-400 mb-1">{p.toUpperCase()}</p>
            <NumInput
              value={subsidy[p]}
              pct
              step={0.01}
              onChange={(v) => setS(`subsidies.${field}.${p}`, v)}
              ariaLabel={`${label} ${p}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
